<template>
  <v-simple-table
    dense
    fixed-header
    light
    :class="['volume-buy-tier-table', { 'service-mode': isServiceProduct }]"
    :height="pricingTableHeight"
  >
    <thead v-if="!multiVolume">
      <tr>
        <th class="text-center carton-price-column">
          <template v-if="isServiceProduct"> PRICE (EX TAX) </template>
          <template v-else>
            CARTON <br />
            PRICE (EX TAX)
          </template>
        </th>
        <th class="text-center number-of-cartons-column">
          <template v-if="isServiceProduct">
            NO. OF <br />
            SERVICE
          </template>
          <template v-else>
            NUMBER <br />
            OF CARTONS
          </template>
        </th>
        <th class="text-center amount-off-column">
          AMOUNT OFF
          <template v-if="!isServiceProduct">
            <br />
            CARTON
          </template>
          (EX TAX)
        </th>
        <template v-if="variableWeightProductSelected">
          <th class="text-center revised-unit-column">
            REVISED COST / KG (EX TAX)
          </th>
          <th v-if="showIncTaxColumns" class="text-center revised-unit-column">
            REVISED COST / KG (INC TAX)
          </th>
        </template>
        <template v-else>
          <th class="text-center revised-unit-column">
            REVISED UNIT <br />COST (EX TAX)
          </th>
          <th v-if="showIncTaxColumns" class="text-center revised-unit-column">
            REVISED UNIT <br />COST (INC TAX)
          </th>
        </template>
        <th
          v-if="showUnitRrpColumns"
          class="text-center volume-buy-unit-column"
        >
          ORIGINAL UNIT RRP <span class="i-color-red">(INC TAX)</span>
        </th>
        <th
          v-if="showUnitRrpColumns"
          class="text-center volume-buy-unit-column"
        >
          <template v-if="variableWeightProductSelected"
            >VOLUME BUY RRP / KG
            <span class="i-color-red">(INC TAX)</span></template
          >
          <template v-else
            >VOLUME BUY <br />
            UNIT RRP <span class="i-color-red">(INC TAX)</span></template
          >
        </th>
        <th v-if="showUnitRrpColumns" class="text-center gpp-column">GP%</th>
        <th class="text-center actions-column">ACTIONS</th>
      </tr>
    </thead>

    <tbody v-for="zone in zonePrices" :key="zone.code">
      <tr>
        <td :colspan="zoneHeaderColspan" class="text-sm-center zone">
          {{ zoneDisplayLabel(zone) }}
        </td>
      </tr>

      <template v-if="zone.tiers">
        <tr
          v-for="tier in zone.tiers"
          :key="tier.numberOfCartons"
          class="text-center"
        >
          <td
            class="carton-price-column"
            v-if="tier === zone.tiers[0]"
            :rowspan="zone.tiers.length + 1"
          >
            {{ $filters.currency(zone.cartonPrice) }}
          </td>
          <td class="cartons number-of-cartons-column">
            {{ tier.numberOfCartons }}
          </td>
          <td class="amount-off-column">
            {{ $filters.currency(tier.amountOff) }}
          </td>
          <td class="revised-unit-column i-td-chip">
            <v-chip class="chip">
              <template v-if="variableWeightProductSelected">
                {{ $filters.currency(tier.revisedTierCostPerKGExTax) }}
              </template>
              <template v-else>
                {{ $filters.currency(tier.revisedTierUnitPriceExTax) }}
              </template>
            </v-chip>
          </td>
          <td v-if="showIncTaxColumns" class="text-center">
            <template v-if="variableWeightProductSelected">
              {{ $filters.currency(tier.revisedTierCostPerKGIncTax) }}
            </template>
            <template v-else>
              {{ $filters.currency(tier.revisedTierUnitPriceIncTax) }}
            </template>
          </td>
          <td v-if="showUnitRrpColumns" class="volume-buy-unit-column">
            {{ $filters.currency(getOriginalProductRrp(zone)) }}
          </td>
          <td v-if="showUnitRrpColumns" class="volume-buy-unit-column">
            {{ $filters.currency(tier.unitRrp) }}
          </td>
          <td v-if="showUnitRrpColumns" class="gpp-column i-td-chip">
            <v-chip
              class="chip"
              :class="{
                negative: tier.grossProfitPercentage < 0,
                positive: tier.grossProfitPercentage > 0
              }"
              >{{ tier.grossProfitPercentage || '-' }}</v-chip
            >
          </td>
          <td class="actions-column">
            <v-btn
              icon
              large
              color="error"
              :disabled="activePromotion"
              v-tooltip="{
                content: 'Remove tier',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click.stop="handleDeleteTier(zone, tier)"
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>

      <tr v-if="!activePromotion && zone.tiers.length <= 2" class="text-center">
        <td
          class="carton-price-column text-only"
          v-if="zone.tiers.length === 0"
        >
          {{ $filters.currency(zone.cartonPrice) }}
        </td>
        <td class="cartons number-of-cartons-column">
          <v-form
            :ref="`formNumberOfCartons${zone.displayName}`"
            @submit.prevent
          >
            <i-numeric-field
              dense
              :disabled="
                activePromotion || isCalculatingProductPriceForRegion(zone.code)
              "
              :error="(error && !zone.newTier.numberOfCartons) || isDuplicate"
              hide-details="auto"
              :label="isServiceProduct ? 'No. of Service' : 'No. Cartons'"
              :loading="isCalculatingProductPriceForRegion(zone.code)"
              :min="0"
              :max="999"
              outlined
              :rules="[
                alertOnEmptyRowFields(zone.newTier.numberOfCartons, [
                  zone.newTier.amountOff,
                  ...(showUnitRrpColumns ? [zone.newTier.unitRrp] : [])
                ]),
                rulesVolumeTierNumberOfCartons(zone.newTier.numberOfCartons)
              ]"
              text-align-right
              v-model="zone.newTier.numberOfCartons"
              @input="onNewTierFieldChange(zone.displayName)"
            >
            </i-numeric-field>
          </v-form>
        </td>
        <td class="amount-off-column">
          <v-form :ref="`formAmountOff${zone.displayName}`" @submit.prevent>
            <i-numeric-field
              dense
              :disabled="
                activePromotion || isCalculatingProductPriceForRegion(zone.code)
              "
              :error="(error && !zone.newTier.amountOff) || isDuplicate"
              hide-details="auto"
              :label="amountOffLabel"
              :loading="isCalculatingProductPriceForRegion(zone.code)"
              :min="0"
              :max="amountOffMax"
              outlined
              :precision="2"
              :prefix="amountOffPrefix"
              :suffix="amountOffSuffix"
              :empty-zero="isPercentageDiscountMode"
              :rules="[
                alertOnEmptyRowFields(zone.newTier.amountOff, [
                  zone.newTier.numberOfCartons,
                  ...(showUnitRrpColumns ? [zone.newTier.unitRrp] : [])
                ]),
                rulesVolumeTierAmountOff(
                  zone.newTier.amountOff,
                  zone.cartonPrice,
                  zone.tiers,
                  zone.newTier.numberOfCartons
                )
              ]"
              text-align-right
              :value="amountOffInputValue(zone.newTier, zone)"
              @input="handleTierAmountOffInput(zone, $event)"
            >
            </i-numeric-field>
            <div
              v-if="isPercentageDiscountMode && zone.newTier.amountOff"
              class="amount-off-helper"
            >
              Amount off: {{ $filters.currency(zone.newTier.amountOff) }}
            </div>
          </v-form>
        </td>
        <td class="revised-unit-column i-td-chip">
          <v-chip class="chip">
            <template v-if="variableWeightProductSelected">
              {{ $filters.currency(zone.newTier.revisedTierCostPerKGExTax) }}
            </template>
            <template v-else>
              {{ $filters.currency(zone.newTier.revisedTierUnitPriceExTax) }}
            </template>
          </v-chip>
        </td>
        <td v-if="showIncTaxColumns" class="revised-unit-column i-td-chip">
          <v-chip class="chip" color="white">
            <template v-if="variableWeightProductSelected">
              {{ $filters.currency(zone.newTier.revisedTierCostPerKGIncTax) }}
            </template>
            <template v-else>
              {{ $filters.currency(zone.newTier.revisedTierUnitPriceIncTax) }}
            </template>
          </v-chip>
        </td>
        <td v-if="showUnitRrpColumns" class="volume-buy-unit-column text-only">
          {{ $filters.currency(getOriginalProductRrp(zone)) }}
        </td>
        <td v-if="showUnitRrpColumns" class="volume-buy-unit-column">
          <v-form :ref="`formUnitRrp${zone.displayName}`" @submit.prevent>
            <i-numeric-field
              dense
              :disabled="
                activePromotion || isCalculatingProductPriceForRegion(zone.code)
              "
              hide-details="auto"
              :label="variableWeightProductSelected ? 'RRP / KG' : 'Unit RRP'"
              :loading="isCalculatingProductPriceForRegion(zone.code)"
              :min="0"
              :max="9999.99"
              outlined
              :precision="2"
              :prefix="currencySymbol"
              :rules="[
                alertOnEmptyRowFields(zone.newTier.unitRrp, [
                  zone.newTier.amountOff,
                  zone.newTier.numberOfCartons
                ]),
                rulesVolumeTierUnitRrp(
                  zone.newTier.unitRrp,
                  zone.newTier.grossProfitPercentage,
                  variableWeightProductSelected
                )
              ]"
              text-align-right
              v-model="zone.newTier.unitRrp"
              @input="
                (calculatePricingForTier(zone.newTier, zone, zone.cartonPrice),
                onNewTierFieldChange(zone.displayName))
              "
              :error="(error && !zone.newTier.unitRrp) || isDuplicate"
            >
            </i-numeric-field>
          </v-form>
        </td>
        <td v-if="showUnitRrpColumns" class="gpp-column i-td-chip">
          <v-chip
            class="chip"
            :class="{
              negative: zone.newTier.grossProfitPercentage < 0,
              positive: zone.newTier.grossProfitPercentage > 0
            }"
            >{{ zone.newTier.grossProfitPercentage || '-' }}</v-chip
          >
        </td>
        <td class="actions-column">
          <v-btn
            color="secondary"
            :disabled="!validTier(zone) || activePromotion"
            icon
            large
            v-tooltip="{
              content: 'Add tier',
              visible: $vuetifyDisplay.mdAndDown ? false : undefined
            }"
            @click.stop="handleAddTier(zone)"
          >
            <v-icon>mdi-plus-circle</v-icon>
          </v-btn>
        </td>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
import { calculateProductPrice } from '@/services/products'
import { DEFAULT_PROMOTION_TIER } from '@/modules/dataMaintenance/helper/VolumePromotionHelper'
import FieldValidators from '@/utils/FieldValidators'
import iNumericField from '@/components/NumericField'
import { mapGetters } from 'vuex'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import {
  amountOffFromDiscountPercentage,
  discountPercentageFromAmountOff,
  DISCOUNT_ENTRY_MODE,
  formatDiscountPercentage,
  setDiscountPercentageFromAmountOff
} from '@/modules/dataMaintenance/helper/DiscountEntryHelper'

const DEBOUNCE_CALC_TIMER = 1500

const ROW_RULES = {
  ALERT_ON_EMPTY_ROW_FIELD: (alertableField, fieldsToValidate = []) => {
    const hasFieldWithValue = fieldsToValidate.find((field) => field > 0)
    return !(hasFieldWithValue && !alertableField) || 'Value required'
  }
}

export default {
  emits: ['change', 'tier-added'],

  components: {
    iNumericField
  },
  props: {
    activePromotion: Boolean,
    error: {
      type: Boolean,
      default: false
    },
    hasGst: undefined,
    hasWet: undefined,
    isDuplicate: Boolean,
    isZoneNewTierValid: Function,
    multiVolume: Boolean,
    packageQuantity: Number,
    products: Array,
    selectedFamilyGroup: Number,
    selectedProduct: String,
    variableWeightTargetInKg: Number,
    zonePrices: Array,
    isServiceProduct: {
      type: Boolean,
      default: false
    },
    discountEntryMode: {
      type: String,
      default: DISCOUNT_ENTRY_MODE.AMOUNT
    }
  },

  data: () => ({
    calculatingProductPriceRegionCodes: [],
    debounceCalculatePrice: [],
    pendingTierCalculations: {},
    ...FieldValidators,
    ...ROW_RULES
  }),

  computed: {
    ...mapGetters('tenant', [
      'tenantCurrency',
      'tenantLocale',
      'tenantCountryRegion'
    ]),

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    pricingTableHeight() {
      return this.selectedProduct || this.selectedFamilyGroup ? '500px' : 'auto'
    },

    variableWeightProductSelected() {
      if (this.variableWeightTargetInKg != null) {
        return Number(this.variableWeightTargetInKg) > 0
      }

      if (!this.selectedProduct) return false

      const product = this.products?.find(
        (p) => p.productCode === this.selectedProduct
      )

      return !!product?.variableWeightTargetInKg
    },
    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },
    isUsTenant() {
      return this.displayRules.isUSCountry
    },
    showIncTaxColumns() {
      return this.displayRules.showTaxSuffix
    },
    showUnitRrpColumns() {
      return !this.isServiceProduct && this.displayRules.showRrp
    },
    isPercentageDiscountMode() {
      return this.discountEntryMode === DISCOUNT_ENTRY_MODE.PERCENTAGE
    },
    amountOffLabel() {
      const baseLabel = this.isPercentageDiscountMode
        ? 'Discount %'
        : 'Amount Off'
      return this.isServiceProduct
        ? `${baseLabel} (ex Tax)`
        : `${baseLabel} Carton (ex Tax)`
    },
    amountOffPrefix() {
      return this.isPercentageDiscountMode ? '' : this.currencySymbol
    },
    amountOffSuffix() {
      return this.isPercentageDiscountMode ? '%' : undefined
    },
    amountOffMax() {
      return this.isPercentageDiscountMode ? 99.99 : 9999.99
    },
    zoneHeaderColspan() {
      if (this.isServiceProduct) {
        return this.showIncTaxColumns ? 6 : 5
      }
      return this.showUnitRrpColumns ? 9 : 5
    }
  },

  methods: {
    zoneDisplayLabel(zone) {
      const displayName = zone.displayName || zone.code || ''
      const name = zone.name
      if (!name || name === displayName || displayName.includes(` - ${name}`)) {
        return displayName
      }
      return `${displayName} - ${name}`
    },

    amountOffInputValue(tier, zone) {
      if (!this.isPercentageDiscountMode) return tier.amountOff
      if (tier.discountPercentage) return tier.discountPercentage
      return formatDiscountPercentage(
        discountPercentageFromAmountOff(zone.cartonPrice, tier.amountOff),
        this.tenantLocale
      )
    },

    handleTierAmountOffInput(zone, value) {
      if (this.isPercentageDiscountMode) {
        zone.newTier.discountPercentage = value
        zone.newTier.amountOff = amountOffFromDiscountPercentage(
          zone.cartonPrice,
          value
        )
      } else {
        zone.newTier.amountOff = value
        setDiscountPercentageFromAmountOff(
          zone.newTier,
          zone.cartonPrice,
          value
        )
      }
      this.handleTierPricingInput(zone)
    },

    getOriginalProductRrp(zone) {
      const zoneOriginalRrp = Number(zone?.originalUnitRrp)
      if (!isNaN(zoneOriginalRrp) && zoneOriginalRrp > 0) return zoneOriginalRrp

      const selectedProductData = this.products.find(
        (p) => p.productCode === this.selectedProduct
      )
      const fallbackRrp = Number(
        selectedProductData?.regionalPriceMap?.[zone.code]?.[0]
          ?.regionUnitRrp ||
          selectedProductData?.regionalPriceMap?.[zone.code]?.[0]?.rrp
      )

      return !isNaN(fallbackRrp) && fallbackRrp > 0 ? fallbackRrp : null
    },

    isCalculatingProductPriceForRegion(pricingRegionCode) {
      return (
        this.calculatingProductPriceRegionCodes.indexOf(pricingRegionCode) !==
        -1
      )
    },

    calculatePricingForTier(tier, zone, cartonPrice) {
      // If amountOff >= cartonPrice -> API throws error because unitPriceExTax becomes negative
      if (tier.amountOff > 0 && tier.amountOff < cartonPrice) {
        this.debounceCalcPricingForTier(tier, zone, cartonPrice)
      } else {
        clearTimeout(this.debounceCalculatePrice[zone.code])
        delete this.pendingTierCalculations[zone.code]
        tier.grossProfitPercentage = null
        tier.revisedTierUnitPriceExTax = null
        tier.revisedTierUnitPriceIncTax = null
        tier.revisedTierCostPerKGExTax = null
        tier.revisedTierCostPerKGIncTax = null
        if (this.isServiceProduct) {
          tier.unitRrp = null
        }
      }
    },

    handleTierPricingInput(zone) {
      this.calculatePricingForTier(zone.newTier, zone, zone.cartonPrice)
      this.onNewTierFieldChange(zone.displayName)
    },

    runTierPriceCalc(regionCode, tier, zone, cartonPrice) {
      delete this.pendingTierCalculations[regionCode]
      this.calculatingProductPrice = true
      if (this.calculatingProductPriceRegionCodes.indexOf(regionCode) === -1) {
        this.calculatingProductPriceRegionCodes.push(regionCode)
      }

      return calculateProductPrice({
        cartonPriceExTax: cartonPrice - tier.amountOff,
        packageQuantity: this.packageQuantity,
        variableWeightTargetInKg: this.variableWeightTargetInKg,
        // srpIncTax is the same as unitRrp
        srpIncTax: tier.unitRrp || null,
        hasGst: this.hasGst,
        hasWet: this.hasWet
      }).then((data) => {
        tier.grossProfitPercentage = data.gpp
        tier.revisedTierUnitPriceExTax = data.unitPriceExTax
        tier.revisedTierUnitPriceIncTax = data.unitPriceIncTax
        tier.revisedTierCostPerKGExTax = data.pricePerKgExTax
        tier.revisedTierCostPerKGIncTax = data.pricePerKgIncTax
        if (this.isServiceProduct) {
          tier.unitRrp = data.unitPriceIncTax
        }
        this.calculatingProductPrice = false
        this.removeCalculatingProductPriceRegionCode(regionCode)
      })
    },

    removeCalculatingProductPriceRegionCode(regionCode) {
      const regionCodeIndex =
        this.calculatingProductPriceRegionCodes.indexOf(regionCode)
      if (regionCodeIndex !== -1) {
        this.calculatingProductPriceRegionCodes.splice(regionCodeIndex, 1)
      }
    },

    debounceCalcPricingForTier(tier, zone, cartonPrice) {
      const regionCode = zone.code
      clearTimeout(this.debounceCalculatePrice[regionCode])
      // Store the immediate-execution form of this calc alongside the debounce
      // timer, so a bulk action (Add ALL Tiers) can flush it on demand instead
      // of racing the still-pending 1500ms wait and silently skipping the zone.
      this.pendingTierCalculations[regionCode] = () =>
        this.runTierPriceCalc(regionCode, tier, zone, cartonPrice)
      this.debounceCalculatePrice[regionCode] = setTimeout(() => {
        const run = this.pendingTierCalculations[regionCode]
        if (run) run()
      }, DEBOUNCE_CALC_TIMER)
    },

    // Add ALL Tiers checks each zone's validity synchronously, but that
    // validity depends on grossProfitPercentage which only lands after the
    // debounced pricing API call above resolves - without flushing first,
    // clicking Add ALL Tiers shortly after typing silently adds nothing,
    // because every zone still looks incomplete at click time.
    flushPendingTierCalculations() {
      const pending = Object.entries(this.pendingTierCalculations)
      pending.forEach(([regionCode]) => {
        clearTimeout(this.debounceCalculatePrice[regionCode])
      })
      return Promise.all(pending.map(([, run]) => run()))
    },

    handleAddTier(zone) {
      zone.tiers.push({
        numberOfCartons: Number(zone.newTier.numberOfCartons),
        amountOff: Number(zone.newTier.amountOff),
        unitRrp: this.isServiceProduct
          ? Number(zone.newTier.revisedTierUnitPriceIncTax)
          : Number(zone.newTier.unitRrp),
        revisedTierUnitPriceExTax: zone.newTier.revisedTierUnitPriceExTax,
        revisedTierUnitPriceIncTax: zone.newTier.revisedTierUnitPriceIncTax,
        revisedTierCostPerKGExTax: zone.newTier.revisedTierCostPerKGExTax,
        revisedTierCostPerKGIncTax: zone.newTier.revisedTierCostPerKGIncTax,
        grossProfitPercentage: this.isServiceProduct
          ? null
          : Number(zone.newTier.grossProfitPercentage)
      })

      zone.tiers = zone.tiers.sort((t1, t2) =>
        t1.numberOfCartons > t2.numberOfCartons ? 1 : -1
      )
      zone.newTier = { ...DEFAULT_PROMOTION_TIER }
      this.$emit('change', this.zonePrices)
      this.$emit('tier-added')
    },

    hasAddableTier(zone) {
      return this.validTier(zone) && !this.activePromotion && !zone.isAdded
    },

    async addAllEligibleTiers() {
      await this.flushPendingTierCalculations()
      this.zonePrices.forEach((zone) => {
        if (this.hasAddableTier(zone)) {
          this.handleAddTier(zone)
        }
      })
    },

    handleDeleteTier(zone, tier) {
      zone.tiers = zone.tiers.filter(
        (t) => t.numberOfCartons !== tier.numberOfCartons
      )
    },

    onNewTierFieldChange(zoneDisplayName) {
      /*
        For each zone newTier, a form wraps the v-text-field element.
        On field change, we trigger form validation manually as
        :rules validator is not reactive unless field clicked first
        in our case we need reactivity even if field not clicked yet
      */
      this.$nextTick(() => {
        this.$refs[`formNumberOfCartons${zoneDisplayName}`][0].validate()
        this.$refs[`formAmountOff${zoneDisplayName}`][0].validate()
        if (
          !this.isServiceProduct &&
          this.$refs[`formUnitRrp${zoneDisplayName}`]
        ) {
          this.$refs[`formUnitRrp${zoneDisplayName}`][0].validate()
        }
      })
    },

    validTier(zone) {
      return this.isZoneNewTierValid(zone)
    },

    /*
      In Multiple Volume Buys, we need to trigger fields validation
      every time tiers table is expanded (mounted) to make easy for
      users to spot what rows are incomplete
    */
    validateAllTiers(refs = {}) {
      const refsArray = [...Object.values(refs)]
      refsArray.forEach((ref) => ref[0].validate(0))
    }
  },

  mounted() {
    this.validateAllTiers(this.$refs)
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.volume-buy-tier-table {
  td {
    &.text-only {
      padding-top: 16px !important;
    }
    height: 55px !important;
    vertical-align: top;
    text-align: center;
    padding: 10px 8px !important;
  }
  .carton-price-column,
  .revised-unit-column,
  .actions-column,
  .gpp-column {
    width: 11.11% !important;
  }
  .number-of-cartons-column,
  .amount-off-column,
  .volume-buy-unit-column {
    width: 11.11% !important;
  }
  &.service-mode {
    .carton-price-column,
    .revised-unit-column,
    .actions-column {
      width: 16.66% !important;
    }
    .number-of-cartons-column,
    .amount-off-column {
      width: 16.66% !important;
    }
  }
  .zone {
    background-color: $bg-primary-color;
    color: black;
    font-weight: bold;
    font-size: 16px;
    letter-spacing: 0.5px;
  }
  .amount-off-helper {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 16px;
    margin-top: 2px;
    text-align: right;
  }
}
</style>
<style lang="scss">
.volume-buy-tier-table {
  .v-text-field__details {
    padding: 0 4px !important;
  }

  .v-messages {
    .v-messages__message {
      font-size: 0.9rem;
    }
  }
}
</style>
